import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, User, Eye, ArrowLeft, Clock, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import { sanitizeHTML } from '../utils/sanitize';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://www.rodekho.in';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs/${slug}`);
      setBlog(response.data);
      fetchRelatedBlogs(response.data.category);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async (category) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs?category=${category}&limit=3`);
      setRelatedBlogs(response.data.filter(b => b.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Error fetching related blogs:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const text = blog?.title;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Article not found</h2>
          <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const readingTime = blog ? Math.max(1, Math.ceil((blog.content?.replace(/<[^>]*>/g, '').length || 0) / 1000)) : 5;
  const canonicalUrl = `${SITE_URL}/blog/${slug}`;
  const ogImage = blog?.featuredImage
    ? (blog.featuredImage.startsWith('/') ? `${SITE_URL}${blog.featuredImage}` : blog.featuredImage)
    : `${SITE_URL}/logo.png`;
  const stripHtml = (html) => html?.replace(/<[^>]*>/g, '').substring(0, 160) || '';
  const metaDescription = blog?.meta_description || blog?.excerpt || stripHtml(blog?.content) || '';

  const articleSchema = blog ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blog.title,
    description: metaDescription,
    image: ogImage,
    author: { '@type': 'Person', name: blog.author || 'RO Dekho Team' },
    publisher: {
      '@type': 'Organization',
      name: 'RO Dekho',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` }
    },
    datePublished: blog.publishedAt || blog.created_at,
    dateModified: blog.updated_at || blog.publishedAt || blog.created_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    keywords: (blog.tags || []).join(', '),
    wordCount: blog.content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0
  } : null;

  const breadcrumbSchema = blog ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: blog.title, item: canonicalUrl }
    ]
  } : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Head */}
      {blog && (
        <Helmet>
          <title>{blog.meta_title || `${blog.title} | RO Dekho Blog`}</title>
          <meta name="description" content={metaDescription} />
          <meta name="keywords" content={(blog.tags || []).join(', ')} />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
          <meta name="author" content={blog.author || 'RO Dekho Team'} />

          {/* Open Graph */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={blog.title} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:alt" content={blog.title} />
          <meta property="og:site_name" content="RO Dekho" />
          <meta property="og:locale" content="en_IN" />
          <meta property="article:published_time" content={blog.publishedAt || blog.created_at} />
          <meta property="article:modified_time" content={blog.updated_at || blog.publishedAt || blog.created_at} />
          <meta property="article:author" content={blog.author || 'RO Dekho Team'} />
          <meta property="article:section" content={blog.category} />
          {(blog.tags || []).map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={blog.title} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={ogImage} />

          {/* Structured Data */}
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      )}

      <Header />

      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-700 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Blog
        </button>
      </div>

      {/* Article Header */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-blue-700 hover:bg-blue-800 text-white">
              {blog.category}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <User size={18} />
                <span>{blog.author || 'RO Dekho Team'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{formatDate(blog.publishedAt || blog.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye size={18} />
                <span>{blog.views || 0} views</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>{readingTime} min read</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.tags && blog.tags.map((tag, index) => (
                <Badge key={index} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 pb-8 border-b border-gray-200">
              <span className="text-gray-600 font-semibold">Share:</span>
              <button 
                onClick={() => shareOnSocial('facebook')}
                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
              >
                <Facebook size={18} />
              </button>
              <button 
                onClick={() => shareOnSocial('twitter')}
                className="p-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full transition-colors"
              >
                <Twitter size={18} />
              </button>
              <button 
                onClick={() => shareOnSocial('linkedin')}
                className="p-2 bg-blue-700 hover:bg-blue-800 text-white rounded-full transition-colors"
              >
                <Linkedin size={18} />
              </button>
              <button 
                onClick={() => shareOnSocial('whatsapp')}
                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <img 
            src={blog.featuredImage} 
            alt={`${blog.title} - ${blog.category || 'RO Dekho Blog'}`}
            loading="eager"
            className="w-full h-96 object-cover rounded-2xl shadow-xl"
          />
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              {blog.content && blog.content.startsWith('<') ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(blog.content) }} />
              ) : (
                <ReactMarkdown>{blog.content}</ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <div className="bg-gray-100 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedBlogs.map((relatedBlog) => (
                  <Card 
                    key={relatedBlog.id}
                    className="hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                  >
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={relatedBlog.featuredImage} 
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <Badge className="mb-3 bg-blue-700 text-white">
                        {relatedBlog.category}
                      </Badge>
                      <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                        {relatedBlog.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {relatedBlog.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-gradient-to-r from-blue-700 to-lime-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Expert Help with Your RO?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our team of certified technicians is ready to help you with installation, maintenance, or repairs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-6 text-lg rounded-lg"
            >
              Book Service Now
            </Button>
            <Button 
              onClick={() => navigate('/products')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 font-bold px-10 py-6 text-lg rounded-lg"
            >
              Browse RO Systems
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
